import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from '../../components/AppIcon';
import DateDisplay from './components/DateDisplay';
import RevealButton from './components/RevealButton';
import InstagramInput from './components/InstagramInput';
import GlobalCounter from './components/GlobalCounter';
import DailyMessageContent from './components/DailyMessageContent';
import MiniTaskContent from './components/MiniTaskContent';
import PollContent from './components/PollContent';
import HumorContent from './components/HumorContent';
import GiveawayContent from './components/GiveawayContent';
import AwarenessContent from './components/AwarenessContent';
import SurpriseContent from './components/SurpriseContent';
import { getTodaysContent } from '../../services/contentService';
import { incrementVisitCount, trackInteraction, getTodaysAnalytics } from '../../services/analyticsService';
import { addParticipant, addGiveawayParticipant } from '../../services/participantService';
import { hasRevealedToday, trackReveal } from '../../services/revealService';

const Homepage = () => {
  const navigate = useNavigate();
  const [isRevealed, setIsRevealed] = useState(false);
  const [canReveal, setCanReveal] = useState(true);
  const [instagramUsername, setInstagramUsername] = useState('');
  const [totalScans, setTotalScans] = useState(0);
  const [currentContent, setCurrentContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeHomepage();
  }, []);

  const initializeHomepage = async () => {
    try {
      await incrementVisitCount();

      const { data: revealData } = await hasRevealedToday();

      if (revealData) {
        setCanReveal(false);
        setIsRevealed(true);
      }

      const { data: contentData, error } = await getTodaysContent();

      if (error) {
        console.error('Error fetching content:', error);
      } else if (contentData) {
        setCurrentContent(contentData);
      }

      const { data: analyticsData } = await getTodaysAnalytics();
      if (analyticsData) {
        setTotalScans(analyticsData?.totalVisits || 0);
      }

      const savedUsername = sessionStorage.getItem('instagramUsername');
      if (savedUsername) {
        setInstagramUsername(savedUsername);
      }
    } catch (error) {
      console.error('Error initializing homepage:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReveal = async () => {
    if (!canReveal || !currentContent) return;

    try {
      await trackReveal(currentContent?.contentType);
      await trackInteraction(currentContent?.contentType);

      setIsRevealed(true);
      setCanReveal(false);
    } catch (error) {
      console.error('Error handling reveal:', error);
    }
  };

  const handleInstagramChange = (value) => {
    setInstagramUsername(value);
    if (value?.trim()) {
      sessionStorage.setItem('instagramUsername', value);
    }
  };

  const handleInteraction = async (type, data) => {
    try {
      if (!currentContent) return;

      await addParticipant({
        instagramUsername: instagramUsername || 'anonymous',
        contentType: currentContent?.contentType,
        interactionData: data,
        date: new Date()?.toISOString()?.split('T')?.[0],
        time: new Date()?.toTimeString()?.split(' ')?.[0]
      });

      if (type === 'participate' && currentContent?.contentType === 'giveaway' && instagramUsername) {
        const result = await addGiveawayParticipant(currentContent?.id, instagramUsername);
        await trackInteraction(currentContent?.contentType);
        return result;
      }

      await trackInteraction(currentContent?.contentType);
    } catch (error) {
      console.error('Error handling interaction:', error);
    }
  };

  const renderContent = () => {
    if (!currentContent) {
      return (
        <div className="text-center p-6 md:p-8 rounded-xl bg-muted/50 border border-border">
          <Icon name="Calendar" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-base md:text-lg text-muted-foreground">
            Bugün için içerik henüz hazırlanmadı
          </p>
        </div>);

    }

    switch (currentContent?.contentType) {
      case 'dailyMessage':
        return (
          <DailyMessageContent
            message={currentContent?.message || currentContent?.title}
            onShare={() => handleInteraction('share', { type: 'dailyMessage' })} />);


      case 'miniTask':
        return (
          <MiniTaskContent
            task={currentContent?.description || currentContent?.task || currentContent?.message}
            completionMessage={currentContent?.completionMessage}
            onComplete={() => handleInteraction('complete', { type: 'miniTask' })} />);


      case 'poll':
        return (
          <PollContent
            question={currentContent?.question}
            options={currentContent?.options || []}
            pollDate={currentContent?.scheduledDate}
            onVote={(option) => handleInteraction('vote', { option })} />);


      case 'humor':
        return (
          <HumorContent
            content={currentContent?.content || currentContent?.message}
            onReact={(emoji) => handleInteraction('react', { emoji })} />);


      case 'giveaway':
        return (
          <GiveawayContent
            title={currentContent?.title}
            description={currentContent?.description}
            prize={currentContent?.prize}
            instagramUsername={instagramUsername}
            onInstagramChange={handleInstagramChange}
            onParticipate={async () => {
              const result = await handleInteraction('participate', { type: 'giveaway' });
              return result;
            }} />);


      case 'awareness':
        return (
          <AwarenessContent
            message={currentContent?.message}
            campaign={currentContent?.campaign}
            onJoin={() => handleInteraction('join', { type: 'awareness' })} />);


      case 'surprise':
        return (
          <SurpriseContent
            title={currentContent?.title}
            content={currentContent?.content}
            actionText={currentContent?.actionText}
            videoUrl={currentContent?.videoUrl}
            surpriseType={currentContent?.surpriseType}
            onAction={() => handleInteraction('action', { type: 'surprise' })} />);


      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-card to-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} color="var(--color-primary)" className="mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Yükleniyor...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-background">
      <div className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm shadow-md border-b border-border">
        <div className="container-safe py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-primary to-destructive flex items-center justify-center">
              <Icon name="Car" size={20} color="white" />
            </div>
            <div>
              <h1 className="text-base md:text-lg lg:text-xl font-heading font-bold text-foreground">Trafikkonya etkileşimli araç kokusu

              </h1>
              <p className="text-xs md:text-sm text-muted-foreground">Trafikte Etkileşim Başlıyor | Her yolculukta yeni içerik

              </p>
            </div>
          </div>
        </div>
      </div>
      <main className="container-safe pt-24 md:pt-28 lg:pt-32 pb-12 md:pb-16 lg:pb-20">
        <div className="max-w-3xl mx-auto">
          <DateDisplay />

          <div className="card mb-6 md:mb-8 lg:mb-10">
            <RevealButton
              onClick={handleReveal}
              isRevealed={isRevealed}
              disabled={!canReveal} />


            {!canReveal && !isRevealed &&
            <div className="text-center p-6 md:p-8 rounded-xl bg-muted/50 border border-border">
                <Icon name="Clock" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-base md:text-lg text-muted-foreground">
                  Bugünün içeriğini zaten gördün!
                </p>
                <p className="text-sm md:text-base text-muted-foreground mt-2">
                  Yarın yeni bir sürpriz için geri gel
                </p>
              </div>
            }

            {isRevealed &&
            <div className="content-reveal animate-content-reveal">
                {renderContent()}

                {(currentContent?.contentType === 'dailyMessage' || currentContent?.contentType === 'humor') &&
              <InstagramInput
                value={instagramUsername}
                onChange={handleInstagramChange}
                onSubmit={(username) => handleInteraction('instagram', { username })}
                disabled={!isRevealed} />

              }
              </div>
            }
          </div>

          <GlobalCounter count={totalScans} />

          <div className="mt-8 md:mt-10 lg:mt-12 text-center">
            <p className="text-xs md:text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} Trafikkonya araç kokusu QR. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </main>
    </div>);

};

export default Homepage;